import { ReactElement, useEffect, useRef, useState } from 'react';
import h337 from 'heatmap.js';
import {
  DashboardDataFilter,
  DeviceAttributesV1,
  DeviceType,
  Group,
  ParametersType
} from '../constants/telosair-interfaces';
import DeviceHover from './DeviceHover';
import { useData } from '../context/data.context';
import { getActiveConfig } from '../_helpers/dashboardHelper';

type DeviceDataAttributes = DeviceAttributesV1 & {
  position: [number, number];
  sensorData: {
    x: number;
    y: number;
    value: number;
  };
};

type DetailedFloorType = {
  onDashboard?: boolean;
  showExpandedView?: boolean;
  selectedGroup: Group;
  selectedGroupName?: string;
  onBack?: () => void;
  shouldHighlight?: boolean;
  heatMap?: boolean;
  selectedParameters: ParametersType[];
  selectedDevice?: number | null;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

/**
 * A Detailed Floor component will render detailed view of a particular floor and list of sensors available on that floor.
 * @param props - Properties of a DetailedFloor Component.
 * @returns - A HTML for Detailed Floor Card.
 */
export default function DetailedFloor(props: DetailedFloorType): ReactElement {
  const {
    selectedGroup,
    showExpandedView = false,
    onDashboard,
    selectedGroupName,
    onBack,
    heatMap,
    selectedParameters,
    selectedDevice,
    shouldHighlight,
    setDataFilter
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const noMapRef = useRef<HTMLDivElement>(null);
  const [resized, setResized] = useState(false);
  const [imageDisplayData, setImageDisplayData] = useState({
    height: 0,
    width: 0,
    offsetTop: 0,
    offsetLeft: 0
  });

  const { deviceDataSummary, clientThresholdSettings } = useData();

  useEffect(() => {
    /**
     * Handles resize of window.
     */
    const handleResize = (): void => {
      setResized(true);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * Sets width and height of the image on load.
   */
  const setWidthAndHeightOfImage = (): void => {
    if (ref.current) {
      const { clientWidth: containerWidth, clientHeight: containerHeight } = ref.current;
      const image = ref.current.querySelector('img');

      if (image) {
        const { naturalWidth: imageWidth, naturalHeight: imageHeight } = image;
        const containerRatio = containerWidth / containerHeight;
        const imageRatio = imageWidth / imageHeight;
        let renderedWidth;
        let renderedHeight;
        if (containerRatio > imageRatio) {
          renderedWidth = containerHeight * imageRatio;
          renderedHeight = containerHeight;
        } else {
          renderedWidth = containerWidth;
          renderedHeight = containerWidth / imageRatio;
        }
        const leftOffset = (containerWidth - renderedWidth) / 2;
        const topOffset = (containerHeight - renderedHeight) / 2;
        setImageDisplayData({
          height: renderedHeight,
          width: renderedWidth,
          offsetLeft: leftOffset,
          offsetTop: topOffset
        });
      }
    }
  };

  /**
   * Build heatmap.
   * @param values - Values of each device.
   * @param selectedParameter - Selected parameter.
   */
  const buildHeatMap = (
    values: DeviceDataAttributes[],
    selectedParameter: ParametersType
  ): void => {
    const container: HTMLElement | null = document.querySelector('#heatmap');
    if (
      container &&
      heatMap &&
      imageDisplayData.width > 0 &&
      imageDisplayData.height > 0 &&
      selectedParameter
    ) {
      container.innerHTML = '';
      const heatmapInstance = h337.create({
        // only container is required, the rest will be defaults
        container,
        useLocalExtrema: true,
        scaleRadius: true,
        radius: 75,
        maxOpacity: 0.5,
        minOpacity: 0.1,
        backgroundColor: 'transparent',
        gradient: {
          // enter n keys between 0 and 1 here
          // for gradient color customization
          '.1': 'green',
          '.5': 'yellow',
          '.95': 'red'
        }
      });
      const allPoints = values.map((item) => item.sensorData.value);

      const activeConfig = getActiveConfig(clientThresholdSettings, DeviceType.MIXED);
      const max = activeConfig?.thresholds[selectedParameter]?.max || Math.max(...allPoints);
      const min = activeConfig?.thresholds[selectedParameter]?.min || 0;
      // heatmap data format
      const data = {
        max,
        min,
        data: values.map((item) => ({ ...item.sensorData }))
      };
      // if you have a set of datapoints always use setData instead of addData
      // for data initialization
      heatmapInstance.setData(data);
    }
  };

  useEffect(() => {
    const selectedParameter = selectedParameters?.length && selectedParameters[0];
    if (imageDisplayData.height > 0 && imageDisplayData.width > 0 && selectedParameter) {
      const data: DeviceDataAttributes[] = [];

      let isNumAdded = false;
      selectedGroup.sensorDetailsList.forEach((item) => {
        const summary = deviceDataSummary[item.serial_number];
        const particleData = summary?.parameters || {};
        // A device silent in the window carries its last known reading, flagged
        // stale; that shows grey on its marker but must not heat the map.
        if (
          selectedParameter &&
          summary?.stale !== true &&
          particleData[selectedParameter] &&
          particleData[selectedParameter] !== -9999
        ) {
          const parameterValue = particleData[selectedParameter];
          data.push({
            ...item,
            position: [
              item.y * imageDisplayData.height + imageDisplayData.offsetTop,
              item.x * imageDisplayData.width + imageDisplayData.offsetLeft
            ],
            sensorData: {
              y: !isNumAdded
                ? Math.round(item.y * imageDisplayData.height + imageDisplayData.offsetTop)
                : item.y * imageDisplayData.height + imageDisplayData.offsetTop,
              x: !isNumAdded
                ? Math.round(item.x * imageDisplayData.width + imageDisplayData.offsetLeft)
                : item.x * imageDisplayData.width + imageDisplayData.offsetLeft,
              value: parameterValue
            }
          });
          isNumAdded = true;
        }
      });
      buildHeatMap(data, selectedParameter);
    }
  }, [selectedGroup, imageDisplayData, selectedParameters, clientThresholdSettings]);

  useEffect(() => {
    if (resized) {
      setWidthAndHeightOfImage();
      setResized(false);
    }
  }, [resized]);

  const hasFloorMap = selectedGroup.floorPlan !== 'default' && !!selectedGroup.floorPlan;
  useEffect(() => {
    if (!hasFloorMap && noMapRef.current) {
      const { clientWidth: containerWidth, clientHeight: containerHeight } = noMapRef.current;
      const imageHeight = 224;
      const imageWidth = 328;
      const containerRatio = containerWidth / containerHeight;
      const imageRatio = imageWidth / imageHeight;
      let renderedWidth;
      let renderedHeight;
      if (containerRatio > imageRatio) {
        renderedWidth = containerHeight * imageRatio;
        renderedHeight = containerHeight;
      } else {
        renderedWidth = containerWidth;
        renderedHeight = containerWidth / imageRatio;
      }
      const leftOffset = (containerWidth - renderedWidth) / 2;
      const topOffset = (containerHeight - renderedHeight) / 2;
      setImageDisplayData({
        height: renderedHeight,
        width: renderedWidth,
        offsetLeft: leftOffset,
        offsetTop: topOffset
      });
    }
  }, [selectedGroup.floorPlan]);

  return (
    <div className="flex flex-1 h-full flex-col">
      {selectedGroupName && (
        <div className={`relative ${onDashboard && 'py-2'}`}>
          <div
            className={`text-gray-700 dark:text-gray-300 flex items-center font-medium ${
              // eslint-disable-next-line no-nested-ternary
              showExpandedView ? 'text-sm font-medium pt-1' : onDashboard ? 'text-xs' : 'text-base '
            }`}
          >
            {onBack && (
              <button
                onClick={onBack}
                type="button"
                className="material-icons-outlined text-xl ml-[-0.625rem]"
              >
                navigate_before
              </button>
            )}
            <span>{selectedGroupName}</span>
            {onDashboard && (
              <span className={`text-primary-300 ${showExpandedView ? 'pl-3' : 'pl-2'}`}>
                {selectedGroup.sensorDetailsList?.length} Devices{' '}
              </span>
            )}
          </div>
          {!onDashboard && (
            <p className="text-gray-800 dark:text-gray-500 text-sm font-semibold pt-2 pb-3">
              {selectedGroup?.sensorDetailsList?.length} Devices{' '}
              <span className="text-primary-500 font-medium border-l-2 border-l-gray-500 pl-1">
                {
                  selectedGroup?.sensorDetailsList.filter(
                    (currrentDevice) => currrentDevice.serial_number >= 200
                  ).length
                }{' '}
                Parameters Critical
              </span>
            </p>
          )}
        </div>
      )}
      <div
        ref={ref}
        className={`relative map-container flex flex-1 overflow-hidden ${
          showExpandedView && 'mx-28 mt-4'
        }`}
      >
        {selectedGroup.sensorDetailsList?.map((currentDevice) => (
          <DeviceHover
            key={currentDevice.id}
            deviceData={currentDevice}
            imageDisplayData={imageDisplayData}
            heatMap={heatMap}
            size={`${showExpandedView ? 'md' : 'sm'}`}
            shouldHighlight={shouldHighlight}
            selectedParameters={selectedParameters}
            selectedDevice={selectedDevice}
            setDataFilter={setDataFilter}
          />
        ))}
        {!hasFloorMap ? (
          <div ref={noMapRef} className="h-full w-full bg-container-800" />
        ) : (
          <img
            onLoad={setWidthAndHeightOfImage}
            src={selectedGroup.floorPlan}
            className={`h-0 min-h-full object-contain w-full rounded ${
              !showExpandedView && 'border border-gray-700'
            }`}
            alt="FLOOR IMG"
          />
        )}
        {heatMap && (
          <div
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
            className="absolute z-20"
          >
            <div id="heatmap" className="h-full w-full" />
          </div>
        )}
      </div>
    </div>
  );
}

// className={`object-contain rounded ${
//   // eslint-disable-next-line no-nested-ternary
//   !showExpandedView ? 'max-h-[8.5rem]' : onDashboard ? 'max-h-[17rem]' : 'max-h-[25rem]'
// } bg-white`}
